import { useMemo, useState } from "react";
import MI from "@/data/mi-counties.json";
import { SENTIMENT_HEX } from "@/lib/format";

export const MOOD_HEX = {
  positive: SENTIMENT_HEX.positive,
  neutral: SENTIMENT_HEX.neutral,
  negative: SENTIMENT_HEX.negative,
};

const W = 720;
const H = 800;
const PAD = 18;
const NO_DATA = "#e9eef5";

// Web-Mercator y, expressed in degree-equivalent units so x and y share one scale.
const mercY = (lat) => (180 / Math.PI) * Math.log(Math.tan(Math.PI / 4 + (lat * Math.PI) / 180 / 2));

function buildProjection() {
  const [minX, minY, maxX, maxY] = MI.bbox;
  const yTop = mercY(maxY);      // mercator y grows with latitude, screen y grows downward
  const yBottom = mercY(minY);
  const spanX = maxX - minX;
  const spanY = yTop - yBottom;
  const scale = Math.min((W - 2 * PAD) / spanX, (H - 2 * PAD) / spanY);
  const offX = (W - spanX * scale) / 2;
  const offY = (H - spanY * scale) / 2;
  const project = ([lng, lat]) => [
    offX + (lng - minX) * scale,
    offY + (yTop - mercY(lat)) * scale,
  ];
  const paths = {};
  const bounds = {};
  for (const c of MI.counties) {
    const all = [];
    paths[c.name] = c.rings
      .map((ring) => ring.map((pt, i) => {
        const p = project(pt);
        all.push(p);
        return `${i ? "L" : "M"}${p[0].toFixed(1)} ${p[1].toFixed(1)}`;
      }).join("") + "Z")
      .join(" ");
    const xs = all.map((p) => p[0]);
    const ys = all.map((p) => p[1]);
    bounds[c.name] = [Math.min(...xs), Math.min(...ys), Math.max(...xs), Math.max(...ys)];
  }
  return { project, paths, bounds };
}

const countyKey = (unit) => (unit || "").replace(/\s+County$/i, "").trim();

/**
 * Real Michigan county boundaries shaded by sentiment — no map key, no tiles, no billing.
 * County fill = mood · circle = voter density · viewBox auto-fits the race's geography.
 */
export function MichiganCountyMap({ units = [], label = "", onSelect }) {
  const { project, paths, bounds } = useMemo(buildProjection, []);
  const [hover, setHover] = useState(null);

  const byCounty = useMemo(() => {
    const m = {};
    for (const u of units) {
      const names = u.counties?.length ? u.counties : [countyKey(u.geo_unit)];
      for (const n of names) if (bounds[n]) m[n] = u;
    }
    return m;
  }, [units, bounds]);

  const view = useMemo(() => {
    const active = Object.keys(byCounty);
    if (!active.length) return `0 0 ${W} ${H}`;
    let [x0, y0, x1, y1] = [Infinity, Infinity, -Infinity, -Infinity];
    for (const n of active) {
      const b = bounds[n];
      x0 = Math.min(x0, b[0]); y0 = Math.min(y0, b[1]);
      x1 = Math.max(x1, b[2]); y1 = Math.max(y1, b[3]);
    }
    const padX = Math.max((x1 - x0) * 0.14, 26);
    const padY = Math.max((y1 - y0) * 0.14, 26);
    x0 -= padX; y0 -= padY; x1 += padX; y1 += padY;
    // keep the map's aspect ratio so counties never look stretched
    const w = x1 - x0, h = y1 - y0, target = W / H;
    if (w / h > target) { const nh = w / target; y0 -= (nh - h) / 2; y1 += (nh - h) / 2; }
    else { const nw = h * target; x0 -= (nw - w) / 2; x1 += (nw - w) / 2; }
    return `${x0.toFixed(1)} ${y0.toFixed(1)} ${(x1 - x0).toFixed(1)} ${(y1 - y0).toFixed(1)}`;
  }, [byCounty, bounds]);

  const maxCount = Math.max(...units.map((u) => u.voter_count || 0), 1);
  const zoom = Number(view.split(" ")[2]) / W || 1;
  const radius = (n) => (7 + Math.sqrt((n || 0) / maxCount) * 19) * zoom;
  const hovered = hover ? byCounty[hover] : null;

  return (
    <div className="relative" data-testid="mi-county-map">
      <svg viewBox={view} className="h-[520px] w-full" role="img"
        aria-label={`Michigan county sentiment map${label ? `, ${label}` : ""}`}>
        <rect x="-4000" y="-4000" width="9000" height="9000" fill="#eaf1fa" />
        {MI.counties.map((c) => {
          const u = byCounty[c.name];
          const active = !!u;
          const strength = active ? Math.min(1, 0.42 + Math.abs(u.sentiment_score || 0) * 0.58) : 0.5;
          return (
            <path
              key={c.name}
              d={paths[c.name]}
              fill={active ? MOOD_HEX[u.mood] || MOOD_HEX.neutral : NO_DATA}
              fillOpacity={strength}
              stroke={active ? "#ffffff" : "#dbe3ec"}
              strokeWidth={(active ? 1.4 : 0.6) * zoom}
              className={active ? "cursor-pointer" : ""}
              onMouseEnter={() => active && setHover(c.name)}
              onMouseLeave={() => setHover(null)}
              onClick={() => active && onSelect?.(c.name)}
              data-testid={active ? `county-shape-${c.name.replace(/\s+/g, "-")}` : undefined}
            >
              <title>{active ? `${u.geo_unit}, ${u.mood} (${u.voter_count.toLocaleString()} voters)` : c.name}</title>
            </path>
          );
        })}

        {units.map((u) => {
          if (u.lat == null || u.lng == null) return null;
          const [cx, cy] = project([u.lng, u.lat]);
          const r = radius(u.voter_count);
          const county = u.counties?.length ? u.counties[0] : countyKey(u.geo_unit);
          return (
            <g key={u.id} onMouseEnter={() => setHover(bounds[county] ? county : null)} onMouseLeave={() => setHover(null)}
              onClick={() => bounds[county] && onSelect?.(county)}
              className="cursor-pointer" data-testid={`geo-bubble-${u.geo_unit.replace(/\s+/g, "-")}`}>
              <circle cx={cx} cy={cy} r={r} fill="#0f2742" fillOpacity="0.85" stroke="#ffffff" strokeWidth={1.5 * zoom} />
              <text x={cx} y={cy + 3.6 * zoom} textAnchor="middle" fontSize={11 * zoom} fontWeight="700" fill="#ffffff">
                {u.voter_count >= 1000 ? `${(u.voter_count / 1000).toFixed(1)}k` : u.voter_count}
              </text>
              {units.length <= 12 && (
                <text x={cx} y={cy + r + 13 * zoom} textAnchor="middle" fontSize={12 * zoom} fontWeight="600" fill="#334155">
                  {countyKey(u.geo_unit)}
                </text>
              )}
            </g>
          );
        })}
      </svg>

      {label && (
        <span className="pointer-events-none absolute left-3 top-2 font-heading text-xs font-bold uppercase tracking-widest text-slate-400"
          data-testid="geo-scope-label">{label}</span>
      )}

      {hovered && (
        <div className="pointer-events-none absolute right-3 top-3 max-w-[240px] rounded-lg border border-border bg-white/95 px-3 py-2 text-xs shadow-lg backdrop-blur"
          data-testid="county-tooltip">
          <p className="font-heading font-bold text-brand-ink">{hovered.geo_unit}</p>
          <p className="capitalize text-brand-mute">{hovered.mood} · score {hovered.sentiment_score}</p>
          <p className="text-brand-mute">{hovered.voter_count.toLocaleString()} voters ({hovered.voter_share}%)</p>
          {hovered.signal_count !== undefined && (
            <p className="text-brand-mute">{hovered.signal_count} local {hovered.signal_count === 1 ? "signal" : "signals"}</p>
          )}
          {hovered.top_issues?.length > 0 && <p className="mt-1 text-brand-ink">{hovered.top_issues.join(", ")}</p>}
        </div>
      )}
    </div>
  );
}
